import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { first } from 'lodash';
import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Dialog } from '@/components/modals/Dialog';
import { Callout } from '@/components/notifications/Callout/Callout';
import { InfoTooltip } from '@/components/poppers/Tooltip/InfoTooltip';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useMissingDocumentsContext } from '@/modules/missingDocuments/contexts/MissingDocumentsContext';
import { useOtherClientPresentationsForHouseholdQuery } from '@/modules/presentation/clientPresentation/graphql/ClientPresentationDesignerV2.generated';
import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';
import { formPost } from '@/utils/formUtils';
import { getNodes } from '@/utils/graphqlUtils';

import HargroveFirmLogo from './HargroveFirmLogo.svg?react';
import LuminaryLogo from './LuminaryLogo.svg?react';

export function HargroveConsultationButton({
  fullWidth = false,
  size = 'sm',
}: {
  fullWidth?: boolean;
  size?: 'sm' | 'md';
}) {
  const enabled = useFeatureFlag('hargrove_referral_ui');
  const { count, loading } = useMissingDocumentsContext();
  const {
    householdId,
    primaryClients,
    loading: householdLoading,
  } = useHouseholdDetailsContext();
  const user = useCurrentUser();
  const canEdit = useCanEditHouseholdData();
  const trackUserEvent = useTrackUserEvent();
  const [isOpen, setIsOpen] = useState(false);
  const [continuedClientId, setContinuedClientId] = useState<string>();
  // Household context orders clients by creation time, then ID. Use the first
  // primary individual when both members of a couple are marked primary.
  const primaryClient = first(primaryClients);

  if (
    !enabled ||
    ((loading || householdLoading) && !isOpen) ||
    count === 0 ||
    !primaryClient ||
    !user?.employee ||
    !canEdit
  ) {
    return null;
  }

  const hasContinued =
    !!primaryClient.hargroveClientID || continuedClientId === primaryClient.id;

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          width: fullWidth ? '100%' : undefined,
        }}
      >
        <Button
          variant={fullWidth ? 'primary' : 'secondary'}
          size={size}
          fullWidth={fullWidth}
          sx={{
            pr: 5,
            pl: fullWidth ? 5 : undefined,
          }}
          onClick={() => {
            if (hasContinued) {
              formPost(
                '/api/v1/hargrove/auth/login',
                { clientProfileId: primaryClient.id },
                '_blank'
              );
              return;
            }
            setIsOpen(true);
            trackUserEvent('estate_insights attorney_connection_opened', {
              householdId,
              clientProfileId: primaryClient.id,
            });
          }}
        >
          <Stack
            component="span"
            direction="row"
            alignItems="center"
            spacing={1.5}
          >
            <Typography
              component="span"
              variant="body-xs"
              sx={{
                color: 'inherit',
                fontWeight: 700,
                pr: 1.5,
                borderRight: '1px solid',
                borderColor: fullWidth
                  ? 'rgba(255, 255, 255, 0.4)'
                  : COLORS.GRAY[200],
              }}
            >
              HARGROVE
            </Typography>
            <Typography
              component="span"
              variant="body-xs"
              sx={{ color: 'inherit' }}
            >
              Connect to an attorney
            </Typography>
          </Stack>
        </Button>
        <Box
          sx={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
          }}
        >
          <InfoTooltip
            title="Connect the client with a Hargrove attorney to discuss planning opportunities, draft documents and more."
            aria-label="About connecting to an attorney"
            iconProps={{
              sx: {
                color: fullWidth
                  ? 'buttons.contrastText'
                  : COLORS.TEXT.SECONDARY,
              },
            }}
          />
        </Box>
      </Box>
      {isOpen && (
        <HargroveConsultationModal
          client={primaryClient}
          onClose={() => setIsOpen(false)}
          onContinue={() => {
            setContinuedClientId(primaryClient.id);
            setIsOpen(false);
          }}
        />
      )}
    </>
  );
}

function HargroveConsultationModal({
  client,
  onClose,
  onContinue,
}: {
  client: { id: string; displayName: string };
  onClose: () => void;
  onContinue: () => void;
}) {
  const [presentationId, setPresentationId] = useState('');
  const { householdId } = useHouseholdDetailsContext();
  const { data, loading, error } = useOtherClientPresentationsForHouseholdQuery(
    {
      skip: !householdId,
      fetchPolicy: 'network-only',
      variables: { householdId: householdId ?? '' },
    }
  );
  useReportError('Failed to load presentations for Hargrove referral', error);
  const presentations = getNodes(data?.clientPresentationV2s);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="hargrove-consultation-heading"
      sx={{ zIndex: zIndexes.POPOVER }}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: 2, py: 1.5 }}
      >
        <DialogTitle
          id="hargrove-consultation-heading"
          variant="title-l"
          sx={{ p: 0, flex: 1, minWidth: 0 }}
        >
          Connect to a Hargrove attorney
        </DialogTitle>
        <CloseButton size="sm" aria-label="Close" onClick={onClose} />
      </Stack>
      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={{ xs: 1.5, sm: 3 }}
            sx={{
              py: 4,
              px: 2,
              bgcolor: COLORS.GRAY[100],
              borderRadius: 1,
              '& > svg': { minWidth: 0, height: 'auto' },
            }}
          >
            <LuminaryLogo width={180} role="img" aria-label="Luminary" />
            <PlusIcon size={16} color={COLORS.GRAY[500]} aria-hidden />
            <HargroveFirmLogo
              width={170}
              role="img"
              aria-label="Hargrove Firm"
            />
          </Stack>
          <Typography variant="body-s">
            Luminary partners with Hargrove Law Firm to offer a free initial
            legal consultation. Click &apos;Continue to Hargrove&apos; to begin
            the consultation process.
          </Typography>
          <SelectInput
            id="hargrove-presentation"
            label="Include presentation (optional)"
            value={presentationId}
            onChange={(event) => setPresentationId(event.target.value)}
            MenuProps={{ sx: { zIndex: zIndexes.POPOVER } }}
            options={presentations.map((presentation) => ({
              value: presentation.id,
              display: presentation.name,
            }))}
            loading={loading}
            disabled={loading || !!error}
            errorMessage={
              error
                ? 'Presentations could not be loaded. You can continue without one.'
                : undefined
            }
            helpText={
              !loading && !error && presentations.length === 0
                ? 'No presentations are available. You can continue without one.'
                : undefined
            }
          />
          <Callout severity="info-low" textVariant="body-s">
            Hargrove will automatically extract and populate client profile data
            from the selected presentation to streamline client onboarding.
          </Callout>
          <Box
            sx={{
              p: 2,
              border: `1px solid ${COLORS.GRAY[200]}`,
              borderRadius: 1,
            }}
          >
            <Typography component="div" variant="eyebrow-xs" sx={{ mb: 1.5 }}>
              What happens next
            </Typography>
            <Typography component="ul" variant="body-s" sx={{ m: 0, pl: 2.5 }}>
              <li>
                Luminary will share basic client information with Hargrove
              </li>
              <li>You will be redirected to the Hargrove web application</li>
              <li>
                Schedule the free consultation in the Hargrove application
              </li>
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            formPost(
              '/api/v1/hargrove/auth/login',
              {
                clientProfileId: client.id,
                ...(presentationId ? { presentationId } : {}),
              },
              '_blank'
            );
            onContinue();
          }}
        >
          Continue to Hargrove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
